import { useTranslation } from 'react-i18next';
import type { ChartPlanet, ChartHouse, ChartAngles } from '../../hooks/useChart';
import styles from './NatalWheel.module.css';

interface NatalWheelProps {
  planets: ChartPlanet[] | null;
  houses: ChartHouse[] | null;
  angles: ChartAngles | null;
  ascendantSign: string;
  dominantPlanet?: string | null;
}

const ZODIAC_ORDER = [
  'aries', 'taurus', 'gemini', 'cancer', 'leo', 'virgo',
  'libra', 'scorpio', 'sagittarius', 'capricorn', 'aquarius', 'pisces',
] as const;

const SIGN_GLYPH: Record<string, string> = {
  aries: '♈', taurus: '♉', gemini: '♊', cancer: '♋',
  leo: '♌', virgo: '♍', libra: '♎', scorpio: '♏',
  sagittarius: '♐', capricorn: '♑', aquarius: '♒', pisces: '♓',
};

const PLANET_GLYPH: Record<string, string> = {
  sun: '☉', moon: '☽', mercury: '☿', venus: '♀',
  mars: '♂', jupiter: '♃', saturn: '♄',
  uranus: '♅', neptune: '♆', pluto: '♇',
  north_node: '☊', south_node: '☋', chiron: '⚷', lilith: '⚸',
};

// Which points the wheel draws. The nodes and Chiron stay off deliberately —
// this small onboarding disc would get crowded — but Black Moon Lilith is part
// of the reveal, so it is drawn alongside the planets.
const VISIBLE_PLANETS = new Set([
  'sun', 'moon', 'mercury', 'venus', 'mars', 'jupiter', 'saturn',
  'uranus', 'neptune', 'pluto', 'lilith',
]);

// SVG geometry: 100x100 viewBox, centered at (50, 50).
const CX = 50;
const CY = 50;
const R_OUTER = 48;
const R_SIGNS_INNER = 42;
const R_HOUSES_OUTER = 42;
const R_HOUSES_INNER = 30;
const R_PLANETS = 36; // ring where planets are drawn
const R_TICK_INNER = 30;
const R_TICK_OUTER = 33;

// In astrology, the Ascendant is conventionally drawn on the left (9 o'clock).
// We rotate the chart so that the ASC degree maps to angle 180° (left side).
// SVG angle 0° = right; we use math convention internally and flip Y at draw.

function polarToCartesian(angleDeg: number, radius: number): { x: number; y: number } {
  // angleDeg = ecliptic longitude in CHART space (0 = ASC, going counter-clockwise)
  // We map this to SVG: ASC = left (180° SVG), counter-clockwise.
  // SVG angle (math convention, then we negate Y since SVG Y grows down):
  const svgAngle = 180 - angleDeg; // ASC=0 -> 180° (left); +CCW
  const rad = (svgAngle * Math.PI) / 180;
  return {
    x: CX + radius * Math.cos(rad),
    y: CY - radius * Math.sin(rad),
  };
}

function describeSector(
  startAngle: number,
  endAngle: number,
  rOuter: number,
  rInner: number,
): string {
  const p1 = polarToCartesian(startAngle, rOuter);
  const p2 = polarToCartesian(endAngle, rOuter);
  const p3 = polarToCartesian(endAngle, rInner);
  const p4 = polarToCartesian(startAngle, rInner);
  const largeArc = Math.abs(endAngle - startAngle) > 180 ? 1 : 0;
  // Sweep direction matches our CCW math convention -> SVG sweep flag = 0
  return [
    `M ${p1.x} ${p1.y}`,
    `A ${rOuter} ${rOuter} 0 ${largeArc} 0 ${p2.x} ${p2.y}`,
    `L ${p3.x} ${p3.y}`,
    `A ${rInner} ${rInner} 0 ${largeArc} 1 ${p4.x} ${p4.y}`,
    'Z',
  ].join(' ');
}

function ascRotated(absPos: number, ascendantAbsPos: number): number {
  // Rotate so that ASC ecliptic longitude maps to chart-angle 0 (which we draw on the left).
  let a = absPos - ascendantAbsPos;
  a = ((a % 360) + 360) % 360;
  return a;
}

export function NatalWheel({
  planets,
  houses,
  angles,
  ascendantSign,
  dominantPlanet,
}: NatalWheelProps): JSX.Element {
  const { t } = useTranslation('flows');
  // Determine ASC absolute position (degrees on ecliptic).
  // Prefer `angles.asc.absolutePosition`; fallback: sign-only ASC -> use sign-start.
  const ascAbs = angles?.asc.absolutePosition
    ?? ZODIAC_ORDER.indexOf(ascendantSign.toLowerCase() as typeof ZODIAC_ORDER[number]) * 30;

  // Sign sectors: each sign is a 30° slice. We compute their start in chart-angle.
  const signs = ZODIAC_ORDER.map((sign, i) => {
    const signStartAbs = i * 30;
    const chartAngle = ascRotated(signStartAbs, ascAbs);
    return { sign, chartAngle, alt: i % 2 === 1 };
  });

  // House cusps: from API if available, else equal-house from ASC.
  const houseCusps: number[] = houses && houses.length === 12
    ? houses.map((h) => ascRotated(h.absolutePosition, ascAbs))
    : Array.from({ length: 12 }, (_, i) => i * 30);

  // Angles: ASC=0, IC=cusp 4, DC=180, MC=cusp 10
  const ascAngle = 0;
  const mcAngle = angles?.mc?.absolutePosition != null ? ascRotated(angles.mc.absolutePosition, ascAbs) : 270;
  const dcAngle = 180;
  const icAngle = angles?.ic?.absolutePosition != null ? ascRotated(angles.ic.absolutePosition, ascAbs) : 90;

  return (
    <div className={styles.wrap}>
      <svg viewBox="0 0 100 100" className={styles.svg} aria-label={t('onboarding.reveal.wheelAria')}>
        {/* Sign sectors (outer ring) */}
        {signs.map(({ sign, chartAngle, alt }) => {
          const end = chartAngle + 30;
          const midAngle = chartAngle + 15;
          const labelPos = polarToCartesian(midAngle, (R_OUTER + R_SIGNS_INNER) / 2);
          return (
            <g key={sign}>
              <path
                d={describeSector(chartAngle, end, R_OUTER, R_SIGNS_INNER)}
                className={`${styles.signSector} ${alt ? styles.signSectorAlt : ''}`}
              />
              <text x={labelPos.x} y={labelPos.y} className={styles.signGlyph}>
                {SIGN_GLYPH[sign]}
              </text>
            </g>
          );
        })}

        {/* Inner rings */}
        <circle cx={CX} cy={CY} r={R_OUTER} className={styles.outerRing} />
        <circle cx={CX} cy={CY} r={R_SIGNS_INNER} className={styles.innerRing} />
        <circle cx={CX} cy={CY} r={R_HOUSES_INNER} className={styles.houseRing} />

        {/* House cusp lines (inner ring only) */}
        {houseCusps.map((cusp, i) => {
          const inner = polarToCartesian(cusp, R_HOUSES_INNER);
          const outer = polarToCartesian(cusp, R_HOUSES_OUTER);
          return (
            <line
              key={`h-${i}`}
              x1={inner.x} y1={inner.y}
              x2={outer.x} y2={outer.y}
              className={styles.houseLine}
            />
          );
        })}

        {/* Cardinal angle lines: ASC/DC horizontal, MC/IC vertical-ish */}
        {([
          { angle: ascAngle, label: 'ASC' },
          { angle: dcAngle, label: 'DC' },
          { angle: mcAngle, label: 'MC' },
          { angle: icAngle, label: 'IC' },
        ] as const).map(({ angle, label }) => {
          const inner = polarToCartesian(angle, R_HOUSES_INNER);
          const outer = polarToCartesian(angle, R_OUTER);
          const labelPos = polarToCartesian(angle, R_OUTER + 4);
          return (
            <g key={label}>
              <line
                x1={inner.x} y1={inner.y}
                x2={outer.x} y2={outer.y}
                className={styles.angleLine}
              />
              <text x={labelPos.x} y={labelPos.y} className={styles.angleLabel}>
                {label}
              </text>
            </g>
          );
        })}

        {/* Planets */}
        {planets?.filter((p) => VISIBLE_PLANETS.has(p.id)).map((p) => {
          const chartAngle = ascRotated(p.absolutePosition, ascAbs);
          const tickInner = polarToCartesian(chartAngle, R_TICK_INNER);
          const tickOuter = polarToCartesian(chartAngle, R_TICK_OUTER);
          const glyphPos = polarToCartesian(chartAngle, R_PLANETS);
          const isDominant = dominantPlanet === p.id;
          return (
            <g key={p.id}>
              <line
                x1={tickInner.x} y1={tickInner.y}
                x2={tickOuter.x} y2={tickOuter.y}
                className={styles.planetTick}
              />
              <circle cx={glyphPos.x} cy={glyphPos.y} r={2.6} className={styles.planetDot} />
              <text
                x={glyphPos.x}
                y={glyphPos.y}
                className={`${styles.planetGlyph} ${isDominant ? styles.planetGlyphDominant : ''}`}
              >
                {PLANET_GLYPH[p.id] ?? '·'}
              </text>
            </g>
          );
        })}
      </svg>
    </div>
  );
}
